import { forwardRef } from "react";
import formatCI from "../../utils/formatCI";

const CONDITION_LABELS = { buna: "Buna", satisfacatoare: "Satisfacatoare", uzura_normala: "Uzura normala" };

function fmtDate(date) {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${d}/${m}/${date.getFullYear()}`;
}

const tipImobilLabels = {
  apartament: "apartamentul",
  casa: "casa",
  spatiu_comercial: "spatiul comercial",
};

const PVPreview = forwardRef(function PVPreview({ data }, ref) {
  const today = fmtDate(new Date());
  const pvDate = data.pvDate ? fmtDate(new Date(data.pvDate)) : today;

  const tipImobilText = data.imobilTip === "altul"
    ? (data.imobilTipAltul || "imobilul")
    : (tipImobilLabels[data.imobilTip] || "imobilul");
  const ascundeCamere = data.imobilTip === "spatiu_comercial" || data.imobilTip === "altul";

  const ciPropFmt = data.proprietarCI ? formatCI(data.proprietarCI) : "___ ___";
  const ciChirFmt = data.chiriasCI ? formatCI(data.chiriasCI) : "___ ___";

  const rooms = data.pvRooms || [];
  const utilities = data.pvUtilities || {};
  const hasUtilities = Object.values(utilities).some((v) => v && String(v).trim());
  const hasPhotos = rooms.some((r) => (r.photos || []).length > 0);
  const hasItems = rooms.some((r) => (r.items || []).length > 0);
  let sectionNr = 3;

  const sectionTitle = {
    fontSize: "15px",
    fontWeight: 700,
    fontFamily: "'Playfair Display', serif",
    color: "#2d2418",
    margin: "28px 0 12px 0",
    paddingBottom: "6px",
    borderBottom: "1px solid #e8e0d4",
    letterSpacing: "0.03em",
    pageBreakAfter: "avoid",
    breakAfter: "avoid",
  };

  const p = {
    fontSize: "14px",
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#3d3225",
    lineHeight: 1.8,
    margin: "0 0 8px 0",
    textAlign: "justify",
    pageBreakInside: "avoid",
    breakInside: "avoid",
  };

  const bold = { fontWeight: 700, color: "#2d2418" };

  const thStyle = {
    padding: "8px 12px",
    backgroundColor: "#f5f0e8",
    borderBottom: "2px solid #e8e0d4",
    fontSize: "12px",
    fontWeight: 700,
    color: "#4a3f35",
    fontFamily: "'DM Sans', sans-serif",
    textAlign: "left",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  };

  const tdStyle = {
    padding: "8px 12px",
    borderBottom: "1px solid #e8e0d4",
    fontSize: "14px",
    fontFamily: "'Georgia', serif",
    color: "#3d3225",
  };

  return (
    <div
      ref={ref}
      style={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: "48px 52px",
        color: "#2d2418",
        border: "1px solid #e8e0d4",
        maxHeight: "600px",
        overflow: "auto",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        position: "relative",
      }}
    >
      {/* Decorative top border */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "4px",
        background: "linear-gradient(90deg, #3a5ba0, #2a4580, #3a5ba0)",
        borderRadius: "12px 12px 0 0",
      }} />

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <p style={{ fontSize: "12px", color: "#8a7e70", margin: "0 0 8px 0", fontFamily: "'Georgia', serif", fontStyle: "italic" }}>
          Anexa nr. ___ la Contractul de Inchiriere nr. ___ din {today}
        </p>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "22px",
          fontWeight: 800,
          color: "#2d2418",
          margin: "0 0 4px 0",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}>
          Proces Verbal de Predare-Primire
        </h2>
        <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: 0 }}>
          Incheiat astazi, {pvDate}
        </p>
      </div>

      <hr style={{ border: "none", borderTop: "2px solid #e8e0d4", margin: "0 0 24px 0" }} />

      {/* Parties */}
      <h3 style={sectionTitle}>1. PARTILE</h3>
      <p style={p}>
        Intre <span style={bold}>{data.proprietarNume || "________________________"}</span>, CNP {data.proprietarCNP || "____________"}, CI seria {ciPropFmt}, domiciliat in {data.proprietarAdresa || "________________________"}, in calitate de <span style={bold}>Proprietar</span>,
      </p>
      <p style={{ ...p, color: "#8a7e70", fontStyle: "italic" }}>si</p>
      <p style={p}>
        <span style={bold}>{data.chiriasNume || "________________________"}</span>, CNP {data.chiriasCNP || "____________"}, CI seria {ciChirFmt}, domiciliat in {data.chiriasAdresa || "________________________"}, in calitate de <span style={bold}>Chirias</span>,
      </p>
      <p style={{ ...p, fontStyle: "italic" }}>
        s-a procedat la predarea-primirea imobilului in urmatoarele conditii:
      </p>

      {/* Property */}
      <h3 style={sectionTitle}>2. IMOBILUL</h3>
      <p style={p}>
        Imobilul de tip <span style={bold}>{tipImobilText}</span> situat in <span style={bold}>{data.imobilAdresa || "________________________"}</span>{!ascundeCamere && data.imobilCamere ? `, compus din ${data.imobilCamere} camere` : ""}, suprafata utila {data.imobilSuprafata || "___"} mp{data.imobilEtaj ? `, etaj ${data.imobilEtaj}` : ""}, predat {data.mobilat ? "mobilat si utilat" : "nemobilat"}.
      </p>
      <p style={p}>
        Starea generala a imobilului la momentul predarii: <span style={bold}>{data.pvStareGenerala || "buna"}</span>{hasPhotos ? " (conform fotografiilor anexate)" : ""}.
      </p>
      {hasPhotos && (
        <p style={p}>
          Fotografiile anexate au fost realizate la data predarii si reflecta starea reala a imobilului.
        </p>
      )}

      {/* Inventory per room */}
      {rooms.length > 0 && (
        <>
          <h3 style={sectionTitle}>{sectionNr++}. INVENTAR PE CAMERE</h3>
          {rooms.map((room) => (
            <div key={room.id} style={{ marginBottom: "24px" }}>
              <h4 style={{
                fontSize: "14px", fontWeight: 700, color: "#c45d3e",
                fontFamily: "'DM Sans', sans-serif", margin: "0 0 8px 0",
              }}>
                {room.label}
              </h4>

              {(room.items || []).length > 0 ? (
                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "8px" }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: "40px" }}>Nr.</th>
                      <th style={thStyle}>Obiect</th>
                      <th style={{ ...thStyle, width: "120px" }}>Stare</th>
                    </tr>
                  </thead>
                  <tbody>
                    {room.items.map((item, idx) => (
                      <tr key={idx}>
                        <td style={tdStyle}>{idx + 1}</td>
                        <td style={tdStyle}>{item.name || "-"}</td>
                        <td style={tdStyle}>{CONDITION_LABELS[item.condition] || item.condition}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{ ...p, fontStyle: "italic", color: "#8a7e70" }}>Fara obiecte inventariate.</p>
              )}

              {/* Photos */}
              {(room.photos || []).length > 0 && (
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "8px" }}>
                  {room.photos.map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt={`${room.label} foto ${i + 1}`}
                      style={{
                        width: "150px", height: "110px", objectFit: "cover",
                        borderRadius: "8px", border: "1px solid #e8e0d4",
                      }}
                    />
                  ))}
                </div>
              )}

              {room.observations && (
                <p style={{ ...p, marginTop: "8px", fontStyle: "italic" }}>
                  Observatii: {room.observations}
                </p>
              )}
            </div>
          ))}
          {data.mobilat && !hasItems && (
            <p style={{ ...p, fontStyle: "italic", color: "#8a7e70", marginTop: "8px" }}>
              Mobilierul si dotarile existente nu sunt inventariate individual.
            </p>
          )}
        </>
      )}

      {/* Utilities */}
      {hasUtilities && (
        <>
          <h3 style={sectionTitle}>{sectionNr++}. CITIRI CONTOARE</h3>
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "16px" }}>
            <thead>
              <tr>
                <th style={thStyle}>Utilitate</th>
                <th style={{ ...thStyle, width: "120px" }}>Index</th>
                <th style={{ ...thStyle, width: "80px" }}>U.M.</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: "Energie electrica", key: "energieElectrica", unit: "kWh" },
                { label: "Gaz natural", key: "gaz", unit: "mc" },
                { label: "Apa calda", key: "apaCalda", unit: "mc" },
                { label: "Apa rece", key: "apaRece", unit: "mc" },
                { label: "Incalzire", key: "incalzire", unit: "Gcal" },
              ].filter((u) => utilities[u.key]).map((u) => (
                <tr key={u.key}>
                  <td style={tdStyle}>{u.label}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{utilities[u.key]}</td>
                  <td style={tdStyle}>{u.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* General observations */}
      {data.pvObservatiiGenerale && (
        <>
          <h3 style={sectionTitle}>{sectionNr++}. OBSERVATII GENERALE</h3>
          <p style={p}>{data.pvObservatiiGenerale}</p>
        </>
      )}

      {/* Legal declaration */}
      <h3 style={sectionTitle}>{sectionNr++}. DECLARATIE</h3>
      <p style={p}>
        Subsemnatii declara ca imobilul a fost predat/primit in starea descrisa mai sus, iar inventarul corespunde situatiei reale de la data predarii. Orice diferente constatate ulterior nu pot fi imputate partii care preda imobilul, daca nu sunt consemnate in prezentul proces-verbal.
      </p>

      {/* Signatures */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        marginTop: "48px", paddingTop: "32px",
        borderTop: "2px solid #e8e0d4",
        pageBreakInside: "avoid", breakInside: "avoid",
      }}>
        <div style={{ textAlign: "center", flex: "0 0 40%" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            AM PREDAT — PROPRIETAR
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {data.proprietarNume || ""}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {pvDate}
          </p>
        </div>
        <div style={{ textAlign: "center", flex: "0 0 40%" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            AM PRIMIT — CHIRIAS
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {data.chiriasNume || ""}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {pvDate}
          </p>
        </div>
      </div>
    </div>
  );
});

export default PVPreview;
